<template>
  <!--
    「요금·결제」의 요금제 지정 창(설계안 3판 §20.5 — 「유료처럼 쓰는 매장」 · §25.5 지정 끝) — 요금제 둘(라디오) · 지정 끝(날짜·시각이 기본,
    무기한은 명시적으로) · 사유(필수). 상세 창에서 열면 매장이 정해져 있고, 지정 중이면 지금 요금제·끝이 채워져 같은 입구로 바꾸거나 늘린다.
    목록의 [매장 지정]에서 열면 매장 선택기로 고른다(행이 아직 없는 매장도). 말·요청은 `utils/subscriptionAdmin` 한 곳.
  -->
  <el-dialog
    :title="$t(actionText)"
    :visible="visible"
    width="460px"
    append-to-body
    data-testid="subscription-grant-dialog"
    :close-on-click-modal="false"
    @update:visible="$emit('update:visible', $event)"
    @open="reset">
    <p class="subscription-grant__intro">{{ $t(text.grantIntro) }}</p>
    <el-form label-position="top" @submit.native.prevent>
      <SubscriptionStoreField
        :fixed-store-cd="storeCd"
        :store-label="storeLabel"
        :value="form.storeCd"
        :active="visible"
        :error="problems.storeCd"
        testid="subscription-grant"
        @change="pickStore" />
      <el-form-item :label="$t(text.grantPlanLabel)" :error="problems.planCode">
        <el-radio-group v-model="form.planCode" data-testid="subscription-grant-plan">
          <el-radio v-for="option in planOptions" :key="option.value" :label="option.value">{{
            $t(option.label)
          }}</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item :label="$t(text.labelGrantUntil)" :error="problems.untilPicked">
        <el-radio-group v-model="form.untilMode" data-testid="subscription-grant-until-mode" @change="clearUntil">
          <el-radio :label="UNTIL.DATE">{{ $t(text.pickMoment) }}</el-radio>
          <el-radio :label="UNTIL.FOREVER">{{ $t(text.grantForever) }}</el-radio>
        </el-radio-group>
        <SubscriptionMomentPicker
          v-if="form.untilMode === UNTIL.DATE"
          v-model="form.untilPicked"
          class="subscription-grant__picker"
          data-testid="subscription-grant-until-picker"
          @change="clearUntil" />
      </el-form-item>
      <el-form-item :label="$t(text.grantReason)" :error="problems.reason">
        <el-input
          v-model="form.reason"
          type="textarea"
          :rows="3"
          data-testid="subscription-grant-reason"
          :placeholder="$t(text.reasonPlaceholder)"></el-input>
      </el-form-item>
    </el-form>
    <p v-if="problem" class="subscription-grant__problem" data-testid="subscription-grant-problem">{{ problem }}</p>
    <span slot="footer">
      <el-button size="small" @click="$emit('update:visible', false)">{{ $t(text.close) }}</el-button>
      <el-button
        size="small"
        type="primary"
        data-testid="subscription-grant-submit"
        :disabled="!storeChosen"
        :loading="busy"
        @click="submit"
        >{{ $t(actionText) }}</el-button
      >
    </span>
  </el-dialog>
</template>

<script>
import SubscriptionMomentPicker from '@/components/subscription/SubscriptionMomentPicker.vue'
import SubscriptionStoreField from '@/components/subscription/SubscriptionStoreField.vue'
import { httpErrorMessage } from '@/plugins/http/policy'
import {
  GRANT_UNTIL_MODE,
  SUBSCRIPTION_PLAN_OPTIONS,
  SUBSCRIPTION_TEXT,
  SUBSCRIPTION_WRITE_CONFIG,
  grantActionText,
  grantFormOf,
  grantRequestOf,
  isStoreCd,
} from '@/utils/subscriptionAdmin'

const noProblems = () => ({ storeCd: '', planCode: '', untilPicked: '', reason: '' })

export default {
  name: 'SubscriptionGrantDialog',

  components: { SubscriptionMomentPicker, SubscriptionStoreField },

  props: {
    visible: { type: Boolean, default: false },
    /** 정해진 매장(상세 창에서 열 때) — `null` 이면 이 창에서 고른다 */
    storeCd: { type: Number, default: null },
    /** 정해진 매장의 이름(「QA 피자 · S2600…」) */
    storeLabel: { type: String, default: '' },
    /** 지금의 지정(상세의 `grant` — 지정 중일 때만) — 바꿀 때 요금제·끝을 미리 채운다 */
    grant: { type: Object, default: null },
  },

  data: function () {
    return {
      text: SUBSCRIPTION_TEXT,
      planOptions: SUBSCRIPTION_PLAN_OPTIONS,
      UNTIL: GRANT_UNTIL_MODE,
      form: Object.assign({ storeCd: null, reason: '' }, grantFormOf(null)),
      problems: noProblems(),
      /** 칸이 아닌 실패(서버 문장) */
      problem: '',
      busy: false,
    }
  },

  computed: {
    /** 「요금제 지정」 / 지정 중이면 「지정 바꾸기」 — 창 제목과 실행 단추가 같은 말 */
    actionText: function () {
      return grantActionText(this.grant)
    },
    /**
     * **매장을 골랐는가** — 정해진 매장(상세)이거나, 선택기에서 고른 이벤트로 받은 매장이 있다. 「고름」 상태는 이 한 곳이고 실행
     * 단추가 이것을 본다 — 고르기 전에는 꺼져 있다(`form.storeCd` 는 [reset]·[pickStore] 만 쓴다)
     */
    storeChosen: function () {
      return isStoreCd(this.form.storeCd)
    },
  },

  methods: {
    /** 열 때마다 새로 — 정해진 매장·지금 지정(요금제·끝)으로 채운다(선택기 갈래는 매장이 비어 있다) */
    reset: function () {
      this.form = Object.assign({ storeCd: this.storeCd, reason: '' }, grantFormOf(this.grant))
      this.problems = noProblems()
      this.problem = ''
    },

    /** 선택기에서 골랐다(비우면 `null`) */
    pickStore: function (storeCd) {
      this.form.storeCd = storeCd
      this.problems.storeCd = ''
    },

    clearUntil: function () {
      this.problems.untilPicked = ''
    },

    /** 지정 — 200 은 그 매장의 구독 상세다(부모가 다시 읽는다). 실패는 이 창 안에서 한 번 말한다 */
    submit: async function () {
      const request = grantRequestOf(this.form, Date.now())
      this.problems = noProblems()
      this.problem = ''
      if (!request.ok) {
        this.problems = Object.assign(noProblems(), { [request.field]: this.$t(request.problem) })
        return
      }

      this.busy = true
      try {
        const response = await this.$axios.post(request.url, request.body, SUBSCRIPTION_WRITE_CONFIG)

        this.$message({ type: 'success', message: this.$t(this.text.grantDone) })
        this.$emit('update:visible', false)
        this.$emit('granted', response && response.data)
      } catch (error) {
        this.problem = httpErrorMessage(error, this.$t(this.text.writeFailed))
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped>
.subscription-grant__intro {
  margin: 0 0 12px;
  color: var(--color-text-regular);
  word-break: keep-all;
}

.subscription-grant__picker {
  margin-top: 8px;
}

.subscription-grant__problem {
  margin: 0;
  color: var(--color-danger);
}
</style>
